import { StudentWorkPlacementJourneyStatus } from '@duke-hq/svc-api-db';
import {
  PRE_PLACEMENT_JOURNEY_STAGES,
  type PrePlacementJourneyStage,
} from '@duke-hq/shared-constants/studentEnrolment';
import { t } from '@lingui/core/macro';

const SETTABLE_STAGES = PRE_PLACEMENT_JOURNEY_STAGES;

export type ChangeableStage = PrePlacementJourneyStage;

export type JourneyStageKey =
  | 'documents'
  | 'ready'
  | 'sourcing'
  | 'interview'
  | 'contracts'
  | 'confirmed'
  | 'evidence'
  | 'outcome';

export type JourneyStage = {
  key: JourneyStageKey;
  label: string;
  journeyStatus?: ChangeableStage;
  displayFor?: StudentWorkPlacementJourneyStatus;
};

export function journeyStages(): JourneyStage[] {
  return [
    {
      key: 'documents',
      label: t`Documents`,
      journeyStatus: StudentWorkPlacementJourneyStatus.AWAITING_CHECKLIST,
    },
    {
      key: 'ready',
      label: t`Ready`,
      journeyStatus: StudentWorkPlacementJourneyStatus.AWAITING_APPROVAL,
    },
    {
      key: 'sourcing',
      label: t`Sourcing`,
      journeyStatus: StudentWorkPlacementJourneyStatus.AWAITING_PLACEMENT,
    },
    {
      key: 'interview',
      label: t`Interview`,
      journeyStatus: StudentWorkPlacementJourneyStatus.INTERVIEWING,
    },
    {
      key: 'contracts',
      label: t`Contracts`,
      journeyStatus: StudentWorkPlacementJourneyStatus.AWAITING_CONTRACT,
    },
    {
      key: 'confirmed',
      label: t`Confirmed`,
      displayFor: StudentWorkPlacementJourneyStatus.PLACED,
    },
    { key: 'evidence', label: t`Evidence` },
    {
      key: 'outcome',
      label: t`Completed`,
      displayFor: StudentWorkPlacementJourneyStatus.COMPLETED,
    },
  ];
}

export function isChangeableStage(status: string): status is ChangeableStage {
  return SETTABLE_STAGES.some((stage) => stage === status);
}

export function changeableStages(
  currentStatus: StudentWorkPlacementJourneyStatus,
): ChangeableStage[] {
  if (!isChangeableStage(currentStatus)) {
    return [];
  }
  return SETTABLE_STAGES.filter((stage) => stage !== currentStatus);
}

export function stageLabel(status: StudentWorkPlacementJourneyStatus): string | null {
  const stage = journeyStages().find(
    (entry) => entry.journeyStatus === status || entry.displayFor === status,
  );

  return stage?.label ?? null;
}
